/* Bridgewater Direct: mobile presentation for the product configurators (phones only, <=767px).
   Presentation only. It never reads or writes prices, variants, cut lists or cart properties;
   the configurator stays the one owner of its state and tells this module what it drew.

   BDConfigMobile.mount(root, opts)
     root               the configurator's root element
     opts.repaint()     asks the configurator to redraw its drawing (same state, new box size)
     opts.canvas        selector of the drawing card that holds the Front / Plan panes
     opts.dims          selector of the element the key dimensions are written into
     opts.openBtn       selector of the "View drawing full screen" button
     opts.tabs          selector of the Front / Plan tab strip inside the canvas
     opts.stage         selector of the drawing stage inside each [data-preview-pane]
     opts.observe       true: notice redraws by watching the drawing's viewBox (no hook needed)
     opts.dimsFrom      element whose content the viewer's footer copies (instead of rows)
     opts.dual          true: Front and Plan are both on show (no tabs). Each view's [data-fs-view]
                        button opens the viewer on that view, the viewer has its own Front / Plan
                        switch, it works on every screen size, and the engine sizes the panes.
   Otherwise the configurator calls api.drawn({ rows: [[label, value], ...] }) after every redraw. */
(function () {
  'use strict';
  const MQ = window.matchMedia('(max-width: 767px)');
  const isPhone = () => MQ.matches;

  /* ---------- Drawing card: size the pane to the drawing's own shape ----------
     The drawing engine fits the artwork to its box; a fixed, short box made a single cubicle a
     sliver. On phones the active pane takes the height the drawing needs at full width, within
     limits, then the engine redraws into it. */
  function paneFitter(canvas, repaint, sel) {
    // Our own redraws (after resizing the pane) are capped so the fit can never loop.
    let passes = 0, ownRedraw = false;
    function clear() { canvas.querySelectorAll('[data-preview-pane]').forEach(p => { p.style.height = ''; }); }
    function drawn() { const own = ownRedraw; ownRedraw = false; fit(!own); }
    function fit(fresh) {
      if (fresh) passes = 0;
      if (!isPhone() || canvas.classList.contains('is-fs')) { clear(); return; }
      const pane = canvas.querySelector('[data-preview-pane].is-active');
      const stage = pane && pane.querySelector(sel.stage);
      const svg = stage && stage.querySelector('svg');
      const vb = svg && svg.viewBox && svg.viewBox.baseVal;
      if (!vb || !vb.width || !vb.height) return;
      const cs = getComputedStyle(stage);
      const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
      const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
      const innerW = stage.clientWidth - padX;
      const want = innerW * vb.height / vb.width + padY;
      // Big enough to read, small enough that the first choice still shows on the first screen.
      const max = Math.max(260, Math.min(420, window.innerHeight * 0.44));
      const h = Math.round(Math.min(max, Math.max(220, want)));
      if (Math.abs(pane.offsetHeight - h) < 6) return;
      pane.style.height = h + 'px';
      if (passes++ < 2) { ownRedraw = true; repaint(); }
    }
    return { fit, drawn, clear };
  }

  /* ---------- Full-screen viewer ----------
     Moves the configurator's own drawing card into a full-screen layer (no second copy, so the
     drawing can never disagree with the configuration), then zooms by narrowing the SVG viewBox,
     which keeps lines and dimension text sharp at every zoom level. */
  function viewer(canvas, repaint, getDims, sel) {
    const MAX = 8;
    let layer, body, dimsEl, mark, open = false, lastVb = '';
    let z = 1, base = null, cx = 0, cy = 0, svg = null;
    const ptrs = new Map();
    let pinch = null, lastTap = 0, tapPt = null;
    let cur = 'front', opener = null;   // dual: the view on screen, and the button to return focus to
    const VIEW_NAME = { front: 'Front view', plan: 'Plan view' };

    function build() {
      layer = document.createElement('div');
      layer.className = 'bdm-fs';
      layer.setAttribute('role', 'dialog');
      layer.setAttribute('aria-modal', 'true');
      layer.setAttribute('aria-label', 'Drawing viewer');
      layer.hidden = true;
      layer.innerHTML =
        '<div class="bdm-fs__bar">' +
          (sel.dual
            ? '<div class="bdm-fs__views" role="group" aria-label="Drawing">' +
                '<button type="button" class="bdm-fs__view" data-v="front" aria-pressed="true">Front</button>' +
                '<button type="button" class="bdm-fs__view" data-v="plan" aria-pressed="false">Plan</button>' +
              '</div>'
            : '<span class="bdm-fs__title">Your drawing</span>') +
          '<button type="button" class="bdm-fs__btn" data-z="out" aria-label="Zoom out">&minus;</button>' +
          '<button type="button" class="bdm-fs__btn" data-z="in" aria-label="Zoom in">+</button>' +
          '<button type="button" class="bdm-fs__btn bdm-fs__btn--text" data-z="reset">Reset</button>' +
          '<button type="button" class="bdm-fs__btn bdm-fs__btn--close" data-z="close" aria-label="Close drawing viewer">&times;</button>' +
        '</div>' +
        '<div class="bdm-fs__body"></div>' +
        '<div class="bdm-fs__foot"><div data-fs-dims></div><p class="bdm-fs__hint">Pinch or double-tap to zoom. Drag to move.</p></div>';
      document.body.appendChild(layer);
      body = layer.querySelector('.bdm-fs__body');
      dimsEl = layer.querySelector('[data-fs-dims]');
      if (sel.dual) layer.classList.add('bdm-fs--dual');
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        layer.querySelector('.bdm-fs__hint').textContent = 'Scroll or double-click to zoom. Drag to move. Esc to close.';
      }
      layer.querySelector('.bdm-fs__bar').addEventListener('click', e => {
        const v = e.target.closest('[data-v]');
        if (v) { setView(v.dataset.v); return; }
        const b = e.target.closest('[data-z]');
        if (!b) return;
        const k = b.dataset.z;
        if (k === 'close') close();
        else if (k === 'reset') reset();
        else zoomAt(k === 'in' ? z * 1.6 : z / 1.6);
      });
      body.addEventListener('pointerdown', down);
      body.addEventListener('pointermove', move);
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(t => body.addEventListener(t, up));
      body.addEventListener('wheel', e => { e.preventDefault(); zoomAt(z * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY); }, { passive: false });
      document.addEventListener('keydown', e => { if (open && e.key === 'Escape') close(); });
      window.addEventListener('popstate', () => { if (open) close(true); });
    }

    // Dual: show one of the two views full screen. The engine redraws it into the new box.
    function setView(v) {
      cur = v === 'plan' ? 'plan' : 'front';
      canvas.dataset.fsView = cur;
      if (layer) {
        // The front pane's own heading wins ("3D view" when its 3D | Front switch is on 3D).
        const head = canvas.querySelector('[data-preview-pane="front"] .cc-view__label'), fb = layer.querySelector('[data-v="front"]');
        if (head && fb) fb.textContent = head.textContent.trim().replace(/ view$/i, '');
        layer.setAttribute('aria-label', cur === 'front' && head ? head.textContent.trim() : VIEW_NAME[cur]);
        layer.querySelectorAll('[data-v]').forEach(b => b.setAttribute('aria-pressed', b.dataset.v === cur ? 'true' : 'false'));
      }
      if (open) { svg = null; base = null; layer.classList.remove('is-zoomed'); repaint(); }
    }
    function activeSvg() {
      const pane = sel.dual ? canvas.querySelector(`[data-preview-pane="${cur}"]`) : canvas.querySelector('[data-preview-pane].is-active');
      // A pane can hold more than one stage (front: 3D | Front); use the one on show.
      const stage = pane && [...pane.querySelectorAll(sel.stage)].find(s => !s.hidden);
      return stage && stage.querySelector('svg');
    }
    // The engine owns the viewBox; read it fresh whenever the drawing changes or the view switches.
    function ensureBase() {
      const s = activeSvg();
      if (s !== svg) { svg = s; base = null; }
      if (!svg) return false;
      if (!base) {
        const v = svg.viewBox.baseVal;
        if (!v || !v.width) return false;
        base = { x: v.x, y: v.y, w: v.width, h: v.height };
        z = 1; cx = base.x + base.w / 2; cy = base.y + base.h / 2;
      }
      return true;
    }
    function apply() {
      if (!svg || !base) return;
      const w = base.w / z, h = base.h / z;
      // Keep the drawing on screen: the view may not leave the drawing's own bounds.
      cx = Math.min(base.x + base.w - w / 2, Math.max(base.x + w / 2, cx));
      cy = Math.min(base.y + base.h - h / 2, Math.max(base.y + h / 2, cy));
      lastVb = `${cx - w / 2} ${cy - h / 2} ${w} ${h}`;
      svg.setAttribute('viewBox', lastVb);
      layer.classList.toggle('is-zoomed', z > 1.01);
    }
    // Screen point -> drawing units for the current view (preserveAspectRatio "meet", centred).
    function toUnits(px, py) {
      const r = svg.getBoundingClientRect(), w = base.w / z, h = base.h / z;
      const s = Math.min(r.width / w, r.height / h);
      const ox = (r.width - w * s) / 2, oy = (r.height - h * s) / 2;
      return { x: cx - w / 2 + (px - r.left - ox) / s, y: cy - h / 2 + (py - r.top - oy) / s, s };
    }
    function zoomAt(nz, px, py) {
      if (!ensureBase()) return;
      nz = Math.min(MAX, Math.max(1, nz));
      const r = svg.getBoundingClientRect();
      if (px == null) { px = r.left + r.width / 2; py = r.top + r.height / 2; }
      const p = toUnits(px, py);
      z = nz;
      const q = toUnits(px, py);
      cx += p.x - q.x; cy += p.y - q.y;
      apply();
    }
    function reset() {
      if (!ensureBase()) return;
      z = 1; cx = base.x + base.w / 2; cy = base.y + base.h / 2;
      apply();
    }

    function down(e) {
      if (e.target.closest(sel.tabs)) return;
      if (!ensureBase()) return;
      try { body.setPointerCapture(e.pointerId); } catch (err) {}
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (ptrs.size === 2) {
        const [a, b] = [...ptrs.values()];
        pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), z };
      } else if (ptrs.size === 1) {
        const now = Date.now();
        if (now - lastTap < 300 && tapPt && Math.hypot(tapPt.x - e.clientX, tapPt.y - e.clientY) < 30) {
          zoomAt(z > 1.5 ? 1 : 2.5, e.clientX, e.clientY);
          lastTap = 0;
        } else { lastTap = now; tapPt = { x: e.clientX, y: e.clientY }; }
      }
    }
    function move(e) {
      const prev = ptrs.get(e.pointerId);
      if (!prev || !base) return;
      const cur = { x: e.clientX, y: e.clientY };
      ptrs.set(e.pointerId, cur);
      if (ptrs.size >= 2 && pinch) {
        const [a, b] = [...ptrs.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinch.d > 0) zoomAt(pinch.z * d / pinch.d, (a.x + b.x) / 2, (a.y + b.y) / 2);
      } else if (ptrs.size === 1 && z > 1) {
        const s = toUnits(cur.x, cur.y).s;
        cx -= (cur.x - prev.x) / s; cy -= (cur.y - prev.y) / s;
        apply();
      }
    }
    function up(e) {
      ptrs.delete(e.pointerId);
      if (ptrs.size < 2) pinch = null;
    }

    function show(which, from) {
      if (open) return;
      if (!layer) build();
      opener = from || null;
      if (sel.dual) setView(which || 'front');
      open = true;
      mark = document.createComment('bdm-canvas');
      canvas.parentNode.insertBefore(mark, canvas);
      body.appendChild(canvas);
      canvas.classList.add('is-fs');
      layer.hidden = false;
      document.documentElement.classList.add('bdm-fs-open');
      try { history.pushState({ bdmFs: 1 }, ''); } catch (e) {}
      svg = null; base = null;
      fill();
      repaint();
      layer.querySelector('[data-z="close"]').focus({ preventScroll: true });
    }
    function close(fromHistory) {
      if (!open) return;
      open = false;
      if (base && svg) { z = 1; apply(); }
      canvas.classList.remove('is-fs');
      mark.parentNode.insertBefore(canvas, mark);
      mark.remove();
      layer.hidden = true;
      layer.classList.remove('is-zoomed');
      document.documentElement.classList.remove('bdm-fs-open');
      svg = null; base = null; ptrs.clear(); pinch = null;
      delete canvas.dataset.fsView;
      if (!fromHistory) { try { if (history.state && history.state.bdmFs) history.back(); } catch (e) {} }
      repaint();
      if (opener) { try { opener.focus({ preventScroll: true }); } catch (e) {} opener = null; }
    }
    function fill() { if (dimsEl) dimsEl.innerHTML = getDims(); }
    // A redraw (new box, or the Front / Plan switch) replaces the viewBox: start from the fitted view.
    function drawn() { if (open) { svg = null; base = null; layer.classList.remove('is-zoomed'); fill(); } }
    // The viewer's own zoom also changes the viewBox; that is not a redraw.
    const ownViewBox = s => open && s.getAttribute('viewBox') === lastVb;
    return { show, close, drawn, ownViewBox, isOpen: () => open };
  }

  function dimsHTML(rows) {
    const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    return (rows || []).map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join('');
  }

  /* ---------- Show-more toggles (FAQ list, related guides) ---------- */
  function toggle(btn, target, cls, labels) {
    btn.addEventListener('click', () => {
      const on = target.classList.toggle(cls);
      btn.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (labels) btn.textContent = on ? labels[1] : labels[0];
    });
  }
  // Site-wide related links: on configurator phones the guide list folds behind one link.
  function compactRelated(label) {
    const rel = document.querySelector('.bd-rel');
    const guides = rel && rel.querySelector('[aria-labelledby="bd-rel-g-guides"]');
    if (!guides || rel.querySelector('.bdm-rel-toggle')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bdm-rel-toggle';
    btn.setAttribute('aria-expanded', 'false');
    btn.textContent = label || 'Need help? View our cubicle buying guides';
    guides.parentNode.insertBefore(btn, guides);
    toggle(btn, rel, 'bdm-guides-open');
  }

  /* ---------- Floating "Configure" bar: the room it takes at the foot of the screen ----------
     Its height plus the gap under it, as --bdm-cta-h, which bd-config-mobile.css keeps clear at
     the end of the page and in the browser's own scrolling (phones). Fires when the bar appears
     (hidden -> shown is a resize) and when its price or name rewraps. */
  function ctaRoom(bar) {
    if (!bar || !('ResizeObserver' in window)) return;
    new ResizeObserver(() => {
      if (bar.hidden) return;
      const h = bar.getBoundingClientRect().height + (parseFloat(getComputedStyle(bar).bottom) || 0);
      if (h > 0) document.documentElement.style.setProperty('--bdm-cta-h', `${Math.ceil(h)}px`);
    }).observe(bar);
  }

  function mount(root, opts) {
    if (!root || root.__bdm) return root && root.__bdm;
    const sel = { tabs: opts.tabs || '.cc-previewtabs', stage: opts.stage || '.cc-stage', dual: !!opts.dual };
    const canvas = root.querySelector(opts.canvas || '.cc-previewcanvas');
    const dimsEl = root.querySelector(opts.dims || '[data-role="m-dims"]');
    let rows = [];
    const fsDims = () => opts.dimsFrom
      ? `<div class="bdm-fs__ribbon">${opts.dimsFrom.innerHTML}</div>`
      : `<dl class="bdm-dims">${dimsHTML(rows)}</dl>`;
    if (canvas) canvas.classList.add('bdm-canvas');
    // Dual: the drawing engine sizes each pane itself (data-param-fit), so no pane fitter.
    const fitter = canvas && !sel.dual ? paneFitter(canvas, opts.repaint, sel) : null;
    const view = canvas ? viewer(canvas, opts.repaint, fsDims, sel) : null;

    const openBtn = root.querySelector(opts.openBtn || '[data-role="m-fullscreen"]');
    if (openBtn && view) openBtn.addEventListener('click', () => view.show());
    if (sel.dual && canvas && view) canvas.querySelectorAll('[data-fs-view]').forEach(b => {
      b.addEventListener('click', e => { e.stopPropagation(); view.show(b.dataset.fsView, b); });
    });
    // Tapping the drawing itself also opens the viewer (not the view tabs, nor the 3D | Front switch).
    if (canvas && view) canvas.addEventListener('click', e => {
      if (!isPhone() || view.isOpen() || e.target.closest(sel.tabs) || e.target.closest('.cc-viewseg, [data-iso-view]')) return;
      const pane = sel.dual && e.target.closest('[data-preview-pane]');
      view.show(pane ? pane.dataset.previewPane : undefined, pane ? pane.querySelector('[data-fs-view]') : null);
    });
    if (canvas) canvas.querySelector(sel.tabs)?.addEventListener('click', () => {
      if (fitter) requestAnimationFrame(() => fitter.fit(true));
    });

    // Sticky pieces sit under the sticky site header.
    const hdr = document.querySelector('.shopify-section.section-bd-header, .shopify-section-header-sticky');
    const setHead = () => root.style.setProperty('--bdm-head', `${hdr ? hdr.offsetHeight : 0}px`);
    setHead(); window.addEventListener('resize', setHead);

    root.querySelectorAll('[data-bdm-more]').forEach(btn => {
      const target = root.querySelector(btn.dataset.bdmMore);
      if (target) toggle(btn, target, 'is-all', [btn.textContent, btn.dataset.bdmLess || 'Show fewer']);
    });
    compactRelated(opts.guidesLabel);
    ctaRoom(root.querySelector('[data-role="sticky-cta"]'));

    MQ.addEventListener ? MQ.addEventListener('change', () => { if (!sel.dual && !isPhone() && view && view.isOpen()) view.close(); fitter && fitter.fit(true); opts.repaint(); })
                        : MQ.addListener(() => { fitter && fitter.fit(true); opts.repaint(); });

    const api = {
      drawn(info) {
        rows = (info && info.rows) || rows;
        if (dimsEl) dimsEl.innerHTML = dimsHTML(rows);
        if (view) view.drawn();
        if (fitter) requestAnimationFrame(() => fitter.drawn());
      }
    };
    // Pages without a redraw hook: a new viewBox on any drawing means it was redrawn.
    if (opts.observe && canvas && 'MutationObserver' in window) {
      let t = 0;
      new MutationObserver(list => {
        if (list.every(m => view && view.ownViewBox(m.target))) return;
        clearTimeout(t);
        t = setTimeout(() => api.drawn(), 60);
      }).observe(canvas, { subtree: true, attributes: true, attributeFilter: ['viewBox'] });
      api.drawn();
    }
    root.__bdm = api;
    return api;
  }

  window.BDConfigMobile = { mount, isPhone };
})();
